import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CloudArrowDownIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components/forms/Buttons/Button';
import { exportStudentResponses } from '../../services/Interviews/StudentResponseService';
import { downloadCeleryFile, getCeleryFile } from '../../services/Files/celeryFilesService';

const POLL_INTERVAL = 2000;
const MAX_POLLS = 60;

export default function ExportResponses({ uuid }) {
  const [loadingFile, setLoadingFile] = useState(false);
  const activeRef = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => () => {
    activeRef.current = false;
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const wait = () => new Promise((resolve) => {
    timerRef.current = setTimeout(resolve, POLL_INTERVAL);
  });

  const handle = async () => {
    setLoadingFile(true);
    try {
      const task = await exportStudentResponses({ interview: uuid });
      for (let attempt = 0; attempt < MAX_POLLS && activeRef.current; attempt += 1) {
        if (attempt > 0) await wait();
        const result = await getCeleryFile(task?.task_id);
        if (result?.status === 'FAILED') throw new Error('The export job failed.');
        if (result?.status === 'COMPLETED' && result?.file) {
          const { blob, filename } = await downloadCeleryFile(result.file);
          const objectUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(objectUrl);
          toast.success('Report export downloaded.');
          return;
        }
      }
      if (activeRef.current) throw new Error('The export took too long. Please try again.');
    } catch (error) {
      if (activeRef.current) toast.error(error?.response?.data?.detail || error?.message || 'Could not export student reports.');
    } finally {
      if (activeRef.current) setLoadingFile(false);
    }
  };

  return <Button className="min-w-fit" text={loadingFile ? 'Preparing export' : 'Export reports'} disabled={loadingFile} Icon={CloudArrowDownIcon} loading={loadingFile} onClick={handle} color="primary-solid" />;
}
